import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../components/firebaseConfig";
import { useAuth } from "../components/authContext";
import { useRequireListing } from "../hooks/useRequireListing";
import ABActivityModal from "../components/ABActivityModal";
import { TimeSelect } from "../components/TimeSelect";
import ArrowBackIos from "@mui/icons-material/ArrowBackIos";

import ABLocationAutocomplete from "../components/ABLocationAutoComplete";
import { APIProvider } from "@vis.gl/react-google-maps";
import { CameraAlt, AccountCircle } from "@mui/icons-material";
import "./EditListing.css";

const API_BASE_URL = process.env.REACT_APP_BASE_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_KEY;

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MIN_SLOT_MINUTES = 30;

// Numeric index <-> label maps (DB uses 0=Sun..6=Sat)
const INDEX_TO_LABEL = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Convert server weekly [{day:0..6,startMinutes,endMinutes},...] -> UI map keyed by your DAYS ("Mon".."Sun") */
function normalizeWeeklyFromServer(serverWeekly = []) {
    // Start with blank UI map in your Mon..Sun order
    const uiMap = Object.fromEntries(DAYS.map(d => [d, { start: "", end: "" }]));

    for (const r of serverWeekly) {
        if (typeof r?.day !== "number" || r.day < 0 || r.day > 6) continue;
        const label = INDEX_TO_LABEL[r.day]; // "Sun".."Sat"
        // Only assign if our UI actually renders this label
        if (uiMap[label]) {
            uiMap[label] = {
                start: minutesToHHMM(r.startMinutes),
                end: minutesToHHMM(r.endMinutes),
            };
        }
    }
    return uiMap;
}

function buildTimeOptions(stepMinutes = 30) {
    const opts = [];
    for (let m = 0; m < 24 * 60; m += stepMinutes) {
        const hh = Math.floor(m / 60), mm = m % 60;
        const value = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
        const ampm = hh >= 12 ? "PM" : "AM";
        const hour12 = ((hh + 11) % 12) + 1;
        const label = `${hour12}:${String(mm).padStart(2, "0")}${ampm}`;
        opts.push({ value, label });
    }
    return opts;
}
function toMinutes(hhmm) { if (!hhmm) return null; const [h, m] = hhmm.split(":").map(Number); return h * 60 + m; }
function minutesToHHMM(mins) {
    if (mins == null || Number.isNaN(mins)) return "";
    const h = Math.floor(mins / 60), m = mins % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
function minutesToLabel(mins) {
    const h = Math.floor(mins / 60), m = mins % 60;
    if (m === 0) return `${h} ${h === 1 ? "hour" : "hours"}`;
    if (m === 30 && h === 0) return "30 minutes";
    return `${(mins / 60).toFixed(1)} hours`;
}

export default function EditListing() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { checking: checkingListing, allowed: hasListing } = useRequireListing();
    const browserTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

    const [authReady, setAuthReady] = useState(false);
    useEffect(() => onAuthStateChanged(auth, (fb) => setAuthReady(true)), []);

    // form state
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [timeZone, setTimeZone] = useState(() => browserTimeZone);

    const [displayName, setDisplayName] = useState("");
    const [bio, setBio] = useState("");
    const [address, setAddress] = useState("");
    const [locationLat, setLocationLat] = useState(null);
    const [locationLng, setLocationLng] = useState(null);

    const [profilePicFile, setProfilePicFile] = useState(null);
    const [profilePicPreview, setProfilePicPreview] = useState("");

    const [activities, setActivities] = useState([]);
    const [deletedActivityIds, setDeletedActivityIds] = useState(new Set());
    const [showActivityModal, setShowActivityModal] = useState(false);
    const [activityToEdit, setActivityToEdit] = useState(null);

    const [availabilityMode, setAvailabilityMode] = useState("anytime");
    const [weeklyAvailability, setWeeklyAvailability] = useState(
        () => Object.fromEntries(DAYS.map(d => [d, { start: "", end: "" }]))
    );

    useEffect(() => {
        if (user === null) {
            navigate("/login", { replace: true, state: { from: "/edit/listing" } });
        }
    }, [user, navigate]);

    // ---- Prefill from server & gate by profileCreated ----
    useEffect(() => {
        if (!user || !hasListing) return;
        let alive = true;
        (async () => {
            try {
                setLoading(true);
                const token = await user.getIdToken();
                const res = await fetch(`${API_BASE_URL}/api/my/edit`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const json = await res.json();
                if (!res.ok) {
                    // 403 means profile not created yet
                    if (res.status === 403) {
                        navigate("/create/listing", { replace: true });
                        return;
                    }
                    throw new Error(json?.error || "Failed to load listing.");
                }
                if (!alive) return;

                // fill basics
                setDisplayName(json.displayName || "");
                setBio(json.bio || "");
                setAddress(json.address || "");
                setProfilePicPreview(json.profilePictureUrl || "");
                const incomingTz = json.timeZone || "";
                // If server only has the default UTC and the browser is different, prefer the browser zone so hosts get anchored correctly.
                setTimeZone(incomingTz && (incomingTz !== "UTC" || browserTimeZone === "UTC") ? incomingTz : browserTimeZone);

                // activities -> modal shape
                const uiActs = (json.activities || []).map(a => ({
                    id: a.id,
                    title: a.title,
                    desc: a.description || "",
                    duration: minutesToLabel(Number(a.durationMinutes || 0)),
                    price: String(a.price ?? ""),
                    location: a.address || "",
                    extraNotes: a.extraCostNote || "",
                    costOptions: {
                        included: a.extraCostType !== "paid_separately",
                        separate: a.extraCostType === "paid_separately",
                    },
                    blurb: (a.description || "").trim().slice(0, 40) + (a.description?.length > 40 ? "…" : ""),
                }));
                setActivities(uiActs);

                // availability weekly array -> day map
                const map = Object.fromEntries(DAYS.map(d => [d, { start: "", end: "" }]));
                const weekly = json.availability?.weekly || []; // [{day,startMinutes,endMinutes}]
                weekly.forEach(r => {
                    if (!r?.day) return;
                    map[r.day] = {
                        start: minutesToHHMM(r.startMinutes),
                        end: minutesToHHMM(r.endMinutes),
                    };
                });
                setAvailabilityMode(json.availability?.mode || "anytime");
                setWeeklyAvailability(
                    normalizeWeeklyFromServer(json.availability?.weekly || [])
                );
            } catch (e) {
                setError(e.message);
            } finally {
                setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [user, navigate, hasListing, browserTimeZone]);

    // ---- Handlers ----
    const onPicChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const reader = new FileReader();
        reader.onload = () => setProfilePicPreview(reader.result);
        reader.readAsDataURL(f);
        setProfilePicFile(f);
    };

    const handleAddOrEditActivity = (data) => {
        setActivities(prev => {
            const next = { ...data, id: data.id || data._id || data.tempId || undefined };
            const idx = next.id ? prev.findIndex(a => a.id === next.id) : -1;
            if (idx > -1) {
                const copy = [...prev];
                copy[idx] = next;
                return copy;
            }
            return [...prev, next];
        });
        // If an activity was previously marked deleted and re-added with the same id, unmark it
        if (data?.id && deletedActivityIds.has(data.id)) {
            setDeletedActivityIds(prev => {
                const copy = new Set(prev);
                copy.delete(data.id);
                return copy;
            });
        }
    };
    const handleDeleteActivity = (id) => {
        setActivities(prev => prev.filter(a => a.id !== id));
        if (id) {
            setDeletedActivityIds(prev => {
                const copy = new Set(prev);
                copy.add(id);
                return copy;
            });
        }
    };

    const parseDuration = (str) => {
        if (!str) return null;
        const [num, unit] = str.split(" ");
        const minutes = parseFloat(num) * (unit?.startsWith("hour") ? 60 : 1);
        return Math.round(minutes);
    };
    function getExtraCostType(opts = {}) {
        if (opts.separate) return "paid_separately";
        return "included";
    }

    const isValid =
        !!displayName.trim() &&
        !!bio.trim() &&
        !!address.trim() &&
        (profilePicPreview || profilePicFile) &&
        activities.length > 0;

    const timeOptions = useMemo(() => buildTimeOptions(30), []);
    const timeOptionsEnd = useMemo(
        () => [...timeOptions, { value: "24:00", label: "12:00AM (next day)" }],
        [timeOptions]
    );

    const handleAvailChange = (day, field, value) => {
        setWeeklyAvailability(prev => {
            const cur = prev[day] || { start: "", end: "" };
            let next = { ...cur, [field]: value };
            if (field === "start") {
                const s = toMinutes(value);
                const e = toMinutes(cur.end);
                if (s != null) {
                    const minE = s + MIN_SLOT_MINUTES;
                    if (e == null || e < minE) next.end = "";
                } else {
                    next.end = "";
                }
            }
            return { ...prev, [day]: next };
        });
    };
    function getEndOptionsForDay(day) {
        const start = weeklyAvailability[day]?.start || "";
        if (!start) return timeOptionsEnd;
        const minEndMins = toMinutes(start) + MIN_SLOT_MINUTES;
        return timeOptionsEnd.filter(opt => toMinutes(opt.value) >= minEndMins);
    }
    function hasAvailRowError(day) {
        const row = weeklyAvailability[day] || { start: "", end: "" };
        return !!row.start && !row.end;
    }

    const onSave = async (e) => {
        e.preventDefault();
        setError("");
        if (!isValid) { setError("Please complete all required fields."); return; }

        // validate custom availability
        if (availabilityMode === "custom") {
            for (const d of DAYS) {
                const { start, end } = weeklyAvailability[d] || {};
                if (start && !end) { setError(`Please select an end time for ${d}.`); return; }
                if (start && end && toMinutes(end) < toMinutes(start) + MIN_SLOT_MINUTES) {
                    setError(`End time on ${d} must be at least 30 minutes after the start.`);
                    return;
                }
            }
        }

        try {
            setSaving(true);
            const cleanedActivities = activities.map(a => ({
                id: a.id, // optional: server may ignore or use for upsert
                title: a.title,
                description: a.desc,
                durationMinutes: parseDuration(a.duration),
                price: parseFloat(a.price),
                extraCostNote: a.extraNotes || "",
                extraCostType: getExtraCostType(a.costOptions),
                address: a.location || "",
            }));
            const deletedIdsPayload = Array.from(deletedActivityIds);

            const availabilityPayload = {
                mode: availabilityMode,             // 'anytime' | 'custom'
                weekly: weeklyAvailability          // { Mon:{start:'HH:mm', end:'HH:mm'}, ... }
            };

            const token = await user.getIdToken();
            const form = new FormData();
            if (profilePicFile) form.append("profilePhoto", profilePicFile);
            const tzToSend = (timeZone === "UTC" && browserTimeZone !== "UTC") ? browserTimeZone : (timeZone || browserTimeZone || "UTC");
            form.append("displayName", displayName.trim());
            form.append("bio", bio.trim());
            form.append("address", address.trim());
            form.append("timeZone", tzToSend);
            form.append("activities", JSON.stringify(cleanedActivities));
            form.append("availability", JSON.stringify(availabilityPayload));
            form.append("deletedActivityIds", JSON.stringify(deletedIdsPayload));
            if (locationLat != null) form.append("locationLat", locationLat);
            if (locationLng != null) form.append("locationLng", locationLng);

            const res = await fetch(`${API_BASE_URL}/api/my/edit`, {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}` },
                body: form,
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json?.error || "Failed to save changes.");
            navigate("/dashboard");
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    if (!authReady || checkingListing) {
        return <div className="el-create-container"><div className="el-create-card">Loading…</div></div>;
    }
    if (!user) return null;

    if (loading) {
        return <div className="el-create-container"><div className="el-create-card">Loading your page…</div></div>;
    }

    return (
        <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['places']}>
            <div className="el-create-container">
                <div className="el-create-card">
                    <button className="pv-back" onClick={() => navigate("/dashboard")} aria-label="Back to dashboard">
                        <ArrowBackIos fontSize="small" />
                        <span>Back</span>
                    </button>
                    <h1 className="el-create-title">Edit Your Page</h1>

                    {/* Your Details */}
                    <section className="el-create-section">
                        <h3 className="el-section-title">Your Details</h3>
                        <p className="el-section-hint">Update your photo, name, bio, and city</p>

                        <div className="el-centered-fields">
                            {/* Profile photo */}
                            <div className="el-row">
                                <label className="el-row-label">Profile Photo</label>
                                <div className="el-row-body">
                                    <div className="el-profile-photo-upload">
                                        <div className="el-photo-circle">
                                            {profilePicPreview ? (
                                                <img src={profilePicPreview} alt="Profile" className="el-profile-photo-preview" />
                                            ) : (
                                                <AccountCircle className="el-photo-icon" fontSize="large" />
                                            )}
                                        </div>
                                        <label className="el-upload-button">
                                            <CameraAlt className="el-camera-icon" />
                                            <span>Change</span>
                                            <input
                                                type="file"
                                                accept="image/jpeg, image/jpg, image/png"
                                                onChange={onPicChange}
                                                className="el-file-input"
                                            />
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Display name */}
                            <div className="el-row">
                                <label className="el-row-label">Display Name</label>
                                <div className="el-row-body el-input-wrapper">
                                    <input
                                        className="el-text-input"
                                        value={displayName}
                                        placeholder="e.g. Sarah"
                                        onChange={(e) => setDisplayName(e.target.value)}
                                        maxLength={30}
                                    />
                                </div>
                            </div>

                            {/* Bio */}
                            <div className="el-row">
                                <label className="el-row-label">Bio</label>
                                <div className="el-row-body el-input-wrapper">
                                    <div className="el-textarea-wrapper">
                                        <textarea
                                            className="el-textarea"
                                            value={bio}
                                            onChange={(e) => setBio(e.target.value)}
                                            maxLength={150}
                                            rows={4}
                                        />
                                        <span className="el-char-counter">{bio.length}/150</span>
                                    </div>
                                </div>
                            </div>

                            {/* Location */}
                            <div className="el-row">
                                <label className="el-row-label">Location</label>
                                <div className="el-row-body el-input-wrapper">
                                    <ABLocationAutocomplete
                                        value={address}
                                        onSelect={(place) => {
                                            setAddress(place.formattedAddress || place.displayName?.text || "");
                                            setLocationLat(place.lat ?? null);
                                            setLocationLng(place.lng ?? null);
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Activities */}
                    <section className="el-create-section">
                        <h3 className="el-section-title">What Can People Book You For?</h3>
                        <p className="el-section-hint">Activities you offer (walks, yoga, dinners, matchas, chats, etc)</p>

                        <div className="el-centered-fields el-centered-activity-box">
                            {activities.map(a => (
                                <div className="el-activity-wrapper" key={a.id}>
                                    <button className="el-delete-button" onClick={() => handleDeleteActivity(a.id)}>x</button>
                                    <div className="el-activity-card">
                                        <div className="el-activity-left">
                                            <div className="el-activity-title">{a.title}</div>
                                            <div className="el-activity-meta">
                                              {`$${Number(a.price || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`} • {a.duration}
                                            </div>
                                            <div className="el-activity-blurb">{a.blurb}</div>
                                        </div>
                                        <button
                                            className="el-btn-tertiary"
                                            onClick={() => { setActivityToEdit(a); setShowActivityModal(true); }}
                                        >
                                            Edit
                                        </button>
                                    </div>
                                </div>
                            ))}

                            <div className="el-center-button">
                                <button
                                    className="el-btn-secondary"
                                    onClick={() => { setActivityToEdit(null); setShowActivityModal(true); }}
                                >
                                    Add New Activity
                                </button>
                            </div>

                            <ABActivityModal
                                open={showActivityModal}
                                onClose={() => setShowActivityModal(false)}
                                onSubmit={handleAddOrEditActivity}
                                initialData={activityToEdit || {}}
                            />
                        </div>
                    </section>

                    {/* Availability */}
                    <section className="el-create-section">
                        <h3 className="el-section-title">When Are You Available?</h3>
                        <p className="el-section-hint">Set your schedule so you get booked at times that work for you.</p>

                        <div className="el-centered-fields el-availability-wrap">
                            <div className="el-availability-choices">
                                <label className="el-bullet-option">
                                    <input
                                        type="radio"
                                        name="availabilityMode"
                                        value="anytime"
                                        checked={availabilityMode === "anytime"}
                                        onChange={() => setAvailabilityMode("anytime")}
                                    />
                                    <span className="el-time-bullet-text">Anytime - You're always available unless already booked</span>
                                </label>
                                <label className="el-bullet-option">
                                    <input
                                        type="radio"
                                        name="availabilityMode"
                                        value="custom"
                                        checked={availabilityMode === "custom"}
                                        onChange={() => setAvailabilityMode("custom")}
                                    />
                                    <span className="el-time-bullet-text">Custom - Pick specific days/times</span>
                                </label>
                            </div>

                            {availabilityMode === "custom" && (
                                <>
                                    <p className="el-section-hint el-hint-sub">
                                        Leaving a day empty means you’re unavailable that day.
                                    </p>

                                    <div className="el-availability-grid">
                                        <div></div>
                                        <div className="el-avail-col-header">Start Time</div>
                                        <div className="el-avail-col-header">End Time</div>

                                        {DAYS.map((day) => {
                                            const rowError = hasAvailRowError(day);
                                            return (
                                                <React.Fragment key={day}>
                                                    <div className="el-avail-day-label">{day}</div>

                                                    <div>
                                                        <TimeSelect
                                                            value={weeklyAvailability[day].start}
                                                            options={timeOptions}
                                                            onChange={(v) => handleAvailChange(day, "start", v)}
                                                            allowClear
                                                        />
                                                    </div>

                                                    <div>
                                                        <TimeSelect
                                                            value={weeklyAvailability[day].end}
                                                            options={getEndOptionsForDay(day)}
                                                            onChange={(v) => handleAvailChange(day, "end", v)}
                                                            disabled={!weeklyAvailability[day].start}
                                                            error={rowError}
                                                            allowClear
                                                        />
                                                        {rowError && (
                                                            <div className="el-field-error">Please choose an end time.</div>
                                                        )}
                                                    </div>
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    </section>

                    {error && <div className="el-error-text">{error}</div>}

                    <div className="el-footer-actions">
                        <button className="el-btn-primary" disabled={!isValid || saving} onClick={onSave}>
                            {saving ? "Saving…" : "Save Changes"}
                        </button>
                    </div>
                </div>
            </div>
        </APIProvider>
    );
}
